/**
 * OAuth Scopes queries for TanStack Query (Vendor App)
 */

import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet } from '../utils/request'

// Type definitions
export interface Scope {
  id: string
  name: string
  description: string
}

export interface CustodyScope {
  value: string
  description: string
  help_text?: string
}

export interface CustodyScopesResponse {
  custody_scopes: CustodyScope[]
}

export interface ScopeListParams {
  [key: string]: any
}

// Cache configuration
const CACHE_TIMES = {
  SCOPES: 30 * 60 * 1000, // 30 minutes - scopes rarely change
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch available OAuth scopes
 */
export const useScopesQuery = (params: MaybeRefOrGetter<ScopeListParams> = {}) => {
  return useQuery({
    queryKey: computed(() => queryKeys.scopeList(toValue(params))),
    queryFn: async (): Promise<Scope[]> => {
      return queryGet<Scope[]>('/v2/oauth/tenant-scopes')
    },
    staleTime: CACHE_TIMES.SCOPES,
  })
}

/**
 * Query hook to fetch available custody scopes for tenant
 */
export const useCustodyScopesQuery = () => {
  return useQuery({
    queryKey: ['custody-scopes'],
    queryFn: async (): Promise<CustodyScope[]> => {
      const response = await queryGet<CustodyScopesResponse>('/v2/oauth/tenant-scopes')
      return response.custody_scopes || []
    },
    staleTime: CACHE_TIMES.SCOPES,
  })
}
